import { Link } from 'react-router-dom';
import { MapPin, Users, ArrowRight, Globe } from 'lucide-react';
import SEO from '@/components/common/SEO';
import { Badge } from '@/components/common';
import { COUNTRY_GUIDES } from '@/data/countryGuides';

export default function CountryGuides() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title="Country Guides for Korean Expats"
        description="Detailed relocation guides for Koreans. Housing, healthcare, banking, culture, and community information by country."
        path="/countries"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="flex items-center gap-3 mb-3">
            <Globe className="w-8 h-8" />
            <h1 className="text-2xl sm:text-3xl font-bold">Country Guides</h1>
          </div>
          <p className="text-emerald-100 text-sm sm:text-base max-w-2xl">
            Comprehensive guides for Korean communities worldwide. Find information on
            cost of living, Korean neighborhoods, visa requirements, and practical tips for living abroad.
          </p>
        </div>
      </div>

      {/* Country Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {COUNTRY_GUIDES.map((country) => (
            <Link
              key={country.code}
              to={`/countries/${country.code.toLowerCase()}`}
              className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-gray-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600 hover:shadow-lg transition-all group"
            >
              {/* Flag and name */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">{country.flag}</span>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {country.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {country.nameKo}
                  </p>
                </div>
              </div>

              {/* Korean population */}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mb-3">
                <Users className="w-4 h-4 text-emerald-500" />
                <span>{country.koreanPopulation} Koreans</span>
              </div>

              {/* Major cities preview */}
              <div className="flex flex-wrap gap-1 mb-4">
                {country.majorCities.slice(0, 2).map((city) => (
                  <Badge key={city.name} variant="gray" size="sm">
                    <MapPin className="w-3 h-3 mr-1" />
                    {city.name.split(',')[0]}
                  </Badge>
                ))}
                {country.majorCities.length > 2 && (
                  <Badge variant="gray" size="sm">
                    +{country.majorCities.length - 2}
                  </Badge>
                )}
              </div>

              {/* View guide link */}
              <div className="flex items-center text-emerald-600 dark:text-emerald-400 text-sm font-medium group-hover:gap-2 transition-all">
                <span>View Guide</span>
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        {/* Quick stats */}
        <div className="mt-12 bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Korean Diaspora at a Glance
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">7M+</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Koreans Worldwide</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">8</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Countries Covered</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">2.5M</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">In USA Alone</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">5K+</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Korean Churches</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
